import { useRef } from "react";

import { cn } from "@lib/utils";

export interface FileUploadProps {
	accept?: string;
	multiple?: boolean;
	onChange?: (file: File | null) => void;
	onBeforeSelect?: (file: File) => boolean | Promise<boolean>;
	children: React.ReactNode;
	className?: string;
	disabled?: boolean;
}

/**
 * Custom file upload: hidden input + clickable trigger. Does not render a native file input visibly.
 * @prop accept - The accept attribute for the input
 * @prop multiple - Whether to allow multiple files
 * @prop onChange - The function to call when a file is selected
 * @prop onBeforeSelect - The function to call before a file is selected
 * @prop children - The children to render as the trigger
 * @prop className - The class name to apply to the component
 * @prop disabled - Whether to disable the component
 */
export const FileUpload = ({
	accept,
	multiple = false,
	onChange,
	onBeforeSelect,
	children,
	className,
	disabled = false,
}: FileUploadProps) => {
	const inputRef = useRef<HTMLInputElement>(null);

	const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0] ?? null;
		if (!file) {
			onChange?.(null);
			e.target.value = "";
			return;
		}
		if (onBeforeSelect) {
			const ok = await onBeforeSelect(file);
			if (!ok) {
				e.target.value = "";
				return;
			}
		}
		onChange?.(file);
		e.target.value = "";
	};

	const handleTriggerClick = () => {
		if (!disabled) inputRef.current?.click();
	};

	return (
		<div className={cn(disabled && "pointer-events-none opacity-50", className)}>
			<input
				ref={inputRef}
				type="file"
				accept={accept}
				multiple={multiple}
				className="sr-only"
				aria-hidden
				tabIndex={-1}
				disabled={disabled}
				onChange={handleChange}
			/>
			<div
				role="button"
				tabIndex={0}
				onClick={handleTriggerClick}
				onKeyDown={(e) => e.key === "Enter" && handleTriggerClick()}
			>
				{children}
			</div>
		</div>
	);
};

FileUpload.displayName = "FileUpload";
